/*
	Pure utility functions for CSV generation.
	No React, no DOM — just data transformation.
*/

/**
 * Get the string key for a column definition.
 * String accessors use themselves; function accessors fall back to Header.
 */
export function getAccessorKey(col) {
	return typeof col.accessor === "string" ? col.accessor : col.Header;
}

/**
 * Resolve a column's value for a given row, applying csvColumnRenderers if present.
 */
export function getValueForRow(col, row, csvColumnRenderers) {
	const accessorKey = getAccessorKey(col);

	let rawValue;
	if (typeof col.accessor === "function") {
		rawValue = col.accessor(row);
	} else {
		rawValue = row[col.accessor];
	}

	if (
		csvColumnRenderers &&
		csvColumnRenderers[accessorKey] &&
		rawValue != null
	) {
		return csvColumnRenderers[accessorKey](rawValue, row);
	}

	// Handle { text, pills } cell shape — combine text and pill labels semicolon-separated
	if (
		rawValue != null &&
		typeof rawValue === "object" &&
		"text" in rawValue
	) {
		const parts = [];
		if (rawValue.text) parts.push(rawValue.text);
		if (rawValue.pills) {
			rawValue.pills.forEach((p) => {
				if (!p.excludeFromCSV) parts.push(p.label);
			});
		}
		return parts.join("; ");
	}

	return rawValue;
}

/**
 * Merge base columns (string accessor only) with extra columns.
 */
export function getAllColumns(columns, extraColumns) {
	// Exclude non-data columns (e.g. action/trash buttons) that have no Header text
	const base = (columns || []).filter(
		(col) =>
			col.Header &&
			typeof col.Header === "string" &&
			col.accessor &&
			typeof col.accessor === "string"
	);
	const extra = extraColumns || [];
	return [...base, ...extra];
}

/**
 * Build a selectedColumns map from a columnVisibility object.
 * Columns not in columnVisibility default to true (e.g. extraColumns).
 */
export function buildColumnSelection(allCols, columnVisibility) {
	const selected = {};
	allCols.forEach((col) => {
		const accessor = getAccessorKey(col);
		selected[accessor] = columnVisibility
			? columnVisibility[accessor] !== false
			: true;
	});
	return selected;
}

/**
 * Generate CSV string content from data, columns, selection, and formatters.
 *
 * @param {Object} options
 * @param {Array}  options.data             - rows to export
 * @param {Array}  options.allColumns       - all column definitions
 * @param {Object} options.selectedColumns  - map of accessor -> boolean
 * @param {Object} [options.csvColumnRenderers] - map of accessor -> (value, row) => string
 * @returns {string} CSV content string, or empty string if nothing to export
 */
export function generateCSVContent({
	data,
	allColumns,
	selectedColumns,
	csvColumnRenderers,
}) {
	const activeCols = allColumns.filter(
		(col) => selectedColumns[getAccessorKey(col)]
	);

	if (activeCols.length === 0 || !data || data.length === 0) {
		return "";
	}

	const headers = activeCols.map((col) => col.csvColumnName || col.Header);
	const rows = data.map((row) =>
		activeCols.map((col) => {
			const val = getValueForRow(col, row, csvColumnRenderers);
			if (val == null) return "";
			const str = "" + val;
			return `"${str.replace(/"/g, '""')}"`;
		})
	);

	let csvContent = headers.map((h) => `"${h}"`).join(",") + "\r\n";
	csvContent += rows.map((r) => r.join(",")).join("\r\n");

	return csvContent;
}

/**
 * Trigger a CSV file download in the browser.
 */
export function downloadCSV(csvContent, fileName) {
	const blob = new Blob([csvContent], {
		type: "text/csv;charset=utf-8;",
	});
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = "LEX-" + (fileName || "export") + ".csv";
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
}
